import { useMutation, useQuery } from '@tanstack/react-query';

import type { Response } from 'api/apiBase';

import {
  createDexSwapTask,
  getDexNativeQuote,
  getDexSwapTask,
  getDexSwapTasks,
  previewDexSwapTask,
} from './api';
import type {
  DexNativeQuote,
  DexSwapCreateRequest,
  DexSwapPreview,
  DexSwapRequest,
  DexSwapTaskDetails,
  DexSwapTaskList,
} from './types';

export const dexSwapTaskQueryKeys = {
  all: ['dex-swap-tasks'] as const,
  list: (pairId: number) => ['dex-swap-tasks', pairId, 'list'] as const,
  detail: (pairId: number, taskId: number) => ['dex-swap-tasks', pairId, 'detail', taskId] as const,
};

export const usePreviewDexSwapTask = (pairId: number) =>
  useMutation<Response<DexSwapPreview>, unknown, DexSwapRequest>({
    mutationKey: ['dex-swap-tasks', pairId, 'preview'],
    mutationFn: body => previewDexSwapTask(pairId, body),
  });

export const useCreateDexSwapTask = (pairId: number) =>
  useMutation<Response<DexSwapTaskDetails>, unknown, DexSwapCreateRequest>({
    mutationKey: ['dex-swap-tasks', pairId, 'create'],
    mutationFn: body => createDexSwapTask(pairId, body),
  });

export const useDexSwapTasks = (pairId: number, refetchInterval = 5000) =>
  useQuery<Response<DexSwapTaskList>>({
    queryKey: dexSwapTaskQueryKeys.list(pairId),
    queryFn: () => getDexSwapTasks(pairId),
    enabled: Boolean(pairId),
    refetchInterval,
    refetchOnWindowFocus: false,
  });

export const useDexSwapTask = (pairId: number, taskId: number) =>
  useQuery<Response<DexSwapTaskDetails>>({
    queryKey: dexSwapTaskQueryKeys.detail(pairId, taskId),
    queryFn: () => getDexSwapTask(pairId, taskId),
    enabled: Boolean(pairId && taskId),
    refetchOnWindowFocus: false,
  });

export const useDexNativeQuote = (pairId: number, enabled: boolean) =>
  useQuery<Response<DexNativeQuote>>({
    queryKey: ['dex-swap-tasks', pairId, 'native-quote'],
    queryFn: () => getDexNativeQuote(pairId),
    enabled: Boolean(pairId) && enabled,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  });
